import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { IGCredentialSet, IGCredentialSetCreate, IGCredentialSetUpdate } from '../types'
import { credentialSetsApi } from '../api'

interface CredentialSetContextType {
  credentialSets: IGCredentialSet[]
  preferredCredentialSet: IGCredentialSet | null
  loading: boolean
  error: string | null
  refreshCredentialSets: () => Promise<void>
  createCredentialSet: (data: IGCredentialSetCreate) => Promise<IGCredentialSet>
  updateCredentialSet: (id: number, data: IGCredentialSetUpdate) => Promise<IGCredentialSet>
  deleteCredentialSet: (id: number) => Promise<void>
  setPreferredCredentialSet: (id: number) => Promise<void>
}

const CredentialSetContext = createContext<CredentialSetContextType | undefined>(undefined)

export const useCredentialSets = () => {
  const context = useContext(CredentialSetContext)
  if (!context) {
    throw new Error('useCredentialSets must be used within a CredentialSetProvider')
  }
  return context
}

interface CredentialSetProviderProps {
  children: ReactNode
}

export const CredentialSetProvider: React.FC<CredentialSetProviderProps> = ({ children }) => {
  const [credentialSets, setCredentialSets] = useState<IGCredentialSet[]>([])
  const [preferredCredentialSet, setPreferredCredentialSet] = useState<IGCredentialSet | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Load credential sets on mount
  const refreshCredentialSets = async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch all credential sets
      const sets = await credentialSetsApi.getAll()
      console.log('Loaded credential sets:', sets)
      setCredentialSets(sets)

      // Fetch user's preferred credential set from backend
      const preferred = await credentialSetsApi.getPreferred()
      console.log('Preferred credential set:', preferred)

      if (preferred) {
        setPreferredCredentialSet(preferred)
      } else if (sets.length > 0) {
        // Fallback: use first credential set if no preferred one
        console.log('No preferred credential set, using first available')
        setPreferredCredentialSet(sets[0])
      } else {
        console.log('No credential sets available')
        setPreferredCredentialSet(null)
      }
    } catch (err: any) {
      console.error('Failed to load credential sets:', err)
      console.error('Error details:', err.response?.data)
      setError(err.response?.data?.detail || 'Failed to load credential sets')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refreshCredentialSets()
  }, [])

  const createCredentialSet = async (data: IGCredentialSetCreate): Promise<IGCredentialSet> => {
    try {
      setError(null)
      const newSet = await credentialSetsApi.create(data)
      setCredentialSets(prev => [...prev, newSet])

      // If this is the first credential set, make it preferred
      if (credentialSets.length === 0) {
        setPreferredCredentialSet(newSet)
        await credentialSetsApi.setPreferred(newSet.id)
      }

      return newSet
    } catch (err: any) {
      console.error('Failed to create credential set:', err)
      setError(err.response?.data?.detail || 'Failed to create credential set')
      throw err
    }
  }

  const updateCredentialSet = async (id: number, data: IGCredentialSetUpdate): Promise<IGCredentialSet> => {
    try {
      setError(null)
      const updatedSet = await credentialSetsApi.update(id, data)

      setCredentialSets(prev =>
        prev.map(set => set.id === id ? updatedSet : set)
      )

      // Update preferred if it's the one being updated
      if (preferredCredentialSet?.id === id) {
        setPreferredCredentialSet(updatedSet)
      }

      return updatedSet
    } catch (err: any) {
      console.error('Failed to update credential set:', err)
      setError(err.response?.data?.detail || 'Failed to update credential set')
      throw err
    }
  }

  const deleteCredentialSet = async (id: number): Promise<void> => {
    try {
      setError(null)
      await credentialSetsApi.delete(id)

      setCredentialSets(prev => prev.filter(set => set.id !== id))

      // If deleting the preferred one, switch to another
      if (preferredCredentialSet?.id === id) {
        const remaining = credentialSets.filter(set => set.id !== id)
        if (remaining.length > 0) {
          setPreferredCredentialSet(remaining[0])
          await credentialSetsApi.setPreferred(remaining[0].id)
        } else {
          setPreferredCredentialSet(null)
        }
      }
    } catch (err: any) {
      console.error('Failed to delete credential set:', err)
      setError(err.response?.data?.detail || 'Failed to delete credential set')
      throw err
    }
  }

  const setPreferredCredentialSetFunc = async (id: number): Promise<void> => {
    try {
      setError(null)
      await credentialSetsApi.setPreferred(id)

      const set = credentialSets.find(s => s.id === id)
      if (set) {
        setPreferredCredentialSet(set)
      }
    } catch (err: any) {
      console.error('Failed to set preferred credential set:', err)
      setError(err.response?.data?.detail || 'Failed to set preferred credential set')
      throw err
    }
  }

  const value: CredentialSetContextType = {
    credentialSets,
    preferredCredentialSet,
    loading,
    error,
    refreshCredentialSets,
    createCredentialSet,
    updateCredentialSet,
    deleteCredentialSet,
    setPreferredCredentialSet: setPreferredCredentialSetFunc,
  }

  return <CredentialSetContext.Provider value={value}>{children}</CredentialSetContext.Provider>
}
